import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import clsx from 'clsx';
import { useBreakpoints } from 'hooks';
import { pick } from 'lodash';

import { CardsGrid, navSections, useRegisterSection } from 'components/OnePageTemplates/common';
import { useDataContext } from 'components/OnePageTemplates/context';

import { hasMeaningfulValues } from 'helpers/hasMeaningfulValues';

import { LargeCard } from '../components/LargeCard';
import { icons } from '../components/LargeCard/LargeCard';
import { ISectionVariant } from '../components/LargeCard/types';

import styles from './MarketAndSalesSection.module.scss';

export const MarketAndSalesSection: React.FC = () => {
  const { isMobile } = useBreakpoints();
  const { data, isExtendedPreview } = useDataContext();
  const { t } = useTranslation();
  const registerNavSection = useRegisterSection(navSections.marketAndSales);

  const sectionData = pick(data, [
    'targetMarket',
    'dynamics',
    'mainCompetitors',
    'targetChannels',
    'revenueStreams',
    'marketingStrategy',
  ]);

  if (!hasMeaningfulValues(sectionData)) {
    return null;
  }

  const cardList = (
    isExtendedPreview
      ? [
          {
            title: t('onePageMapping.targetMarketConsump'),
            content: data.targetMarket,
            icon: icons.targetMarket,
          },
          {
            title:t('onePageMapping.marketDynamics'),
            content: data.dynamics,
            icon: icons.marketDynamics,
          },
          {
            title:t('onePageMapping.mainCompetitors'),
            content: data.mainCompetitors,
            icon: icons.mainCompetitors,
          },
          {
            title:t('onePageMapping.targetAudience'),
            content: data.targetChannels,
            icon: icons.targetAudience,
          },
          {
            title:t('onePageMapping.businessModel'),
            content: data.revenueStreams,
            icon: icons.businessModel,
          },
          {
            title: t('onePageMapping.salesStrategy'),
            content: data.marketingStrategy,
            icon: icons.salesStrategy,
          },
        ]
      : [
          {
            title:t('onePageMapping.targetMarket'),
            content: data.targetMarket,
            icon: icons.targetMarket,
          },
          {
            title: t('competition'),
            content: data.mainCompetitors,
            icon: icons.mainCompetitors,
          },
          {
            title:t('onePageMapping.businessModel'),
            content: data.revenueStreams,
            icon: icons.businessModel,
          },
          {
            title:t('onePageMapping.salesAndMarketing'),
            content: data.marketingStrategy,
            icon: icons.salesStrategy,
          },
        ]
  ).filter((el) => Boolean(el.content));

  const rangeVariant: ISectionVariant[] = ['primary', 'secondary', 'primary'];
  const chessVariant: ISectionVariant[] = ['primary', 'secondary', 'secondary', 'primary', 'primary', 'secondary'];
  const lineVariant: ISectionVariant[] = ['primary', 'primary', 'secondary', 'secondary', 'secondary'];
  const mobileVariant: ISectionVariant[] = [
    'primary',
    'secondary',
    'primary',
    'secondary',
    'primary',
    'secondary',
    'primary',
    'secondary',
  ];

  const currVariant = isMobile
    ? mobileVariant
    : cardList.length === 3
      ? rangeVariant
      : cardList.length === 5
        ? lineVariant
        : chessVariant;

  return (
    <Box {...registerNavSection} className={clsx(styles.section, { [styles.isExtendedPreview]: isExtendedPreview })}>
      <Box className={styles.wrapper}>
        <Box className={styles.title}>{t('chapters.MARKET_AND_SALES')}</Box>
        <CardsGrid>
          {cardList.map((card, i) => (
            <LargeCard {...card} key={card.title} variant={currVariant[i]} className={styles.card} />
          ))}
        </CardsGrid>
      </Box>
    </Box>
  );
};
